<template>
  <div
    :class="[imageLoaded, floatImage]"
    class="epi-picture"
  >
    <div
      v-if="!image || failed"
      class="empty-image-placeholder"
    >
      <svgicon
        name="Misc/EmptyPlaceholder"
        class="empty-image-placeholder__icon"
      />
    </div>
    <picture
      v-else-if="usePicture && !dynamicDimensions && loaded"
      v-edit="edit"
    >
      <source
        :srcset="getCloudinaryUrl(image.small, sizeSettings.small)"
        media="(max-width: 48em)"
      >
      <source
        :srcset="getCloudinaryUrl(image.medium, sizeSettings.medium)"
        media="(max-width: 64em)"
      >
      <img
        :src="getCloudinaryUrl(image.large, sizeSettings.large)"
        :alt="description || ''"
      >
    </picture>
    <img
      v-else-if="usePicture && dynamicDimensions && loaded"
      :src="getDynamicCloudinaryUrl()"
      :alt="description || ''"
    >
    <div
      v-else
      v-edit="edit"
      :class="currentSize"
      :style="backgroundImageStyle"
      class="epi-background-container"
      :role="description ? 'img' : null"
      :aria-label="description"
    />
    <img
      v-if="!loaded"
      ref="lazyLoadImage"
      class="lazy-load-image"
      alt="Bild laddas"
      @load="lazyLoadComplete"
      @error="lazyLoadFailed"
    >
  </div>
</template>

<script>
import commonPictureConfig from './commonPictureConfig'
import createCloudinaryUrl from './createCloudinaryUrl.vue'
import '#/Misc/EmptyPlaceholder'

export default {
  mixins: [commonPictureConfig, createCloudinaryUrl],
  props: {
    alt: {
      type: String,
      default: null
    },
    edit: {
      type: String,
      default: ''
    },
    autoAspectRatio: {
      type: Boolean,
      default: true
    },
    // When set to true, will disregard dimensions specified in the sizes-props in favor
    // of the actual dimensions of the container.
    dynamicDimensions: {
      type: Boolean,
      default: false
    },
    floatSetting: {
      type: String,
      default: 'Default'
    }
  },
  data () {
    return {
      failed: false,
      loaded: false,
      watcher: null,
      hasMounted: false
    }
  },
  computed: {
    description () {
      return this.image ? this.image.altText || this.alt : this.alt
    },
    backgroundImageStyle () {
      let ar = (this.autoAspectRatio !== false || this.usePicture) ? `padding-bottom:${this.getImageRatio()}%;` : ''
      let backgroundImage = this.loaded ? `background-image:url("${this.currentImageUrl}");` : ''

      return `${backgroundImage}${ar}`
    },
    currentImageUrl () {
      if (this.dynamicDimensions) {
        return this.getDynamicCloudinaryUrl()
      } else {
        return this.getCloudinaryUrl(this.image[this.currentSize], this.sizeSettings[this.currentSize])
      }
    },
    hasImage () {
      return !!this.image
    },
    imageLoaded () {
      return this.loaded ? 'loaded' : ''
    },
    floatImage () {
      let float = this.floatSetting.toLowerCase()
      return float != 'default' ? `float-image-${float}` : ''
    }
  },
  watch: {
    image () {
      if (this.hasImage) {
        this.loaded = false
        this.failed = false
        this.$nextTick(() => {
          this.createScrollmonitor()
        })
      }
    }
  },
  mounted () {
    this.hasMounted = true
  },
  methods: {
    getImageRatio () {
      const currentSizes = (this.sizes[this.currentSize].height && this.sizes[this.currentSize].width) ? this.sizes[this.currentSize] : this.image[this.currentSize]
      const currentImageDimensions = this.getDimensions(currentSizes)
      return (currentImageDimensions.h / currentImageDimensions.w) * 100
    }
  }
}
</script>

<style lang="scss">
@import './commonPictureStyles.scss';

.epi-picture {
  &.float-image-left {
    float: left;

    .wysiwyg & {
      margin: 0 1.5rem 1.5rem 1rem;

      @include media-breakpoint-up(md) {
        margin: 0 1.5rem 1.5rem 10.33333%;
      }

      @include media-breakpoint-up(lg) {
        margin: 0 1.5rem 1.5rem 12.378959%;
      }
    }

    &::after {
      clear: both;
    }
  }

  &.float-image-right {
    float: right;

    .wysiwyg & {
      margin: 0 1rem 1.5rem 1.5rem;

      @include media-breakpoint-up(md) {
        margin: 0 10.33333% 1.5rem 1.5rem;
      }

      @include media-breakpoint-up(lg) {
        margin: 0 12.378959% 1.5rem 1.5rem;
      }
    }

    &::after {
      clear: both;
    }
  }

  picture {
    // Weird bottom margin fix
    line-height: normal;
  }
}
</style>

